import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OwnershipLevel } from '@fitness-force/enum';
import { AppErrors, BadRequestError, NotFoundError } from '@fitness-force/models';;
import type { Campaign, CampaignById, CampaignRequest, CreateAutomation, UpdateAutomation } from '@fitness-force/models';
import { environment } from '@fitness-force/environments';
import { Observable, catchError, map, throwError } from 'rxjs';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';

@Injectable({
  providedIn: 'root'
})
export class CampaignService {
  private http = inject(HttpClient);
  private router = inject(Router);

  BASE_URL: string = environment.BASE_URL;
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  

  CreateCampaigns(payload:CampaignRequest[]): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANY.CREATE_CAMPAIGN;
    return this.http.post(url, payload, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  GetCampaignById(tenantId:number, campaignId:number): Observable<CampaignById> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.CAMPAIGN.GET_CAMPAIGN
      .replace('{tenant_id}', tenantId.toString())
      .replace('{campaign_id}', campaignId.toString());
    return this.http.get(url, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  GetCampaignList(tenantId:number,allparams: any): Observable<Campaign> {
    let parsedParams = new HttpParams()
    allparams.forEach((p: any) => {
      parsedParams = parsedParams.append(p.name, p.value);
    });
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.CAMPAIGN.GET_CAMPAIGNS
      .replace('{tenant_id}', tenantId.toString());
    return this.http.get(url, {
      headers: this.httpOptions.headers,params: parsedParams
    }).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  GetAutomationList(ownershiplevel: OwnershipLevel,tenantId:number= 0,franchiseId:number=0): Observable<any> {
    let url:string="";
    if(ownershiplevel == OwnershipLevel.Tenant) 
     url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANY.GET_TENANT_AUTOMATIONS
       .replace('{tenant_id}', tenantId.toString());
    if(ownershiplevel == OwnershipLevel.Company) 
     url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANY.GET_COMPANY_AUTOMATIONS;
    if(ownershiplevel == OwnershipLevel.Franchise) 
    url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANY.GET_FRANCHISE_AUTOMATIONS
      .replace('{franchise_id}', franchiseId.toString());
    return this.http.get(url, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  CreateAutomation(payload:CreateAutomation[]): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANY.CREATE_AUTOMATION;
    return this.http.post(url, payload, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  } 

  UpdateAutomation(guid:string, payload:UpdateAutomation): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANY.UPDATE_AUTOMATION
      .replace('{uuid}', guid);
    return this.http.put(url, payload, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  updateAutomationStatus(guid: string, status: string): Observable<any> {
    const activeUrl = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANY.MARK_AUTOMATION_ACTIVE
      .replace('{uuid}', guid);
    const inactiveUrl = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANY.MARK_AUTOMATION_INACTIVE
      .replace('{uuid}', guid);
    const url = status.toLowerCase() === 'active' ? activeUrl : inactiveUrl;
    return this.http.post(url, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        } 
        return throwError(() => new AppErrors(error));
      })
    );
  }

  updateCampaignStatus(guid: string, status: string): Observable<any> {
    const activeUrl = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANY.MARK_CAMPAIGN_ACTIVE
      .replace('{campaign_guid}', guid);
    const inactiveUrl = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANY.MARK_CAMPAIGN_INACTIVE
      .replace('{campaign_guid}', guid);
    const url = status.toLowerCase() === 'active' ? activeUrl : inactiveUrl;
    return this.http.post(url, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => {
        return res;
        }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  stopCampaign(campaignGuid :string): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANY.MARK_CAMPAIGN_STOP
      .replace('{campaign_guid}', campaignGuid);
    return this.http.post(url, { headers: this.httpOptions.headers }).pipe( 
      map((res: any) => {
        return res;
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  voidCampaign(campaignGuid :string): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANY.MARK_CAMPAIGN_VOID
      .replace('{campaign_guid}', campaignGuid);
    return this.http.post(url, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  CreateWebhookAutomation(ownership:OwnershipLevel,tenantId:number,franchiseId:number, payload:any): Observable<any> {
    let url:string="";
    switch(ownership){
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.CAMPAIGN.CREATE_WEBHOOK_AUTOMATION
          .replace('{tenant_id}', tenantId.toString());
        break;
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.GET_WEBHOOK_AUTOMATION;
        break;  
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.GET_WEBHOOK_AUTOMATION
          .replace('{franchise_group_id}', franchiseId.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.CAMPAIGN.CREATE_WEBHOOK_AUTOMATION
          .replace('{tenant_id}', tenantId.toString());
        break;
    }
    return this.http.post(url, payload, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res  
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error)) 
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

}
